import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { includes, isObject, isEmpty, noop, round, size, get, toNumber } from 'lodash';
import SimpleBar from 'simplebar-react';

// local dependencies
import { checkbox as Checkbox } from 'ui/views/generic/checkbox';
import { getLabel } from 'utils/getLabel';
import { textWithPatternMatch } from 'utils/textWithPatternMatch';
import { SELECT_ALL_OPTION_VALUE, GLOBAL_LABELS, DATA_TYPES } from 'constants/common';
import { searchInput as SearchBar } from 'ui/views/generic/searchInput';
import { spinner as Spinner } from 'ui/views/generic/spinner';
import { EndScrollDetector } from 'modules/end-scroll-detector';
import { ResizableBox } from 'react-resizable';
import reactMergeRefs from 'react-merge-refs';

// choice option
const ChoiceOption = ( props ) => {

    // check if option is selected
    const isSelected = props.isOptionSelected( props.option );

    // compute class names
    const widgetClassName = classnames( 'ui-choice-list__options__option', {
        'ui-choice-list__options__option--active': props.activeIndex === props.option.index || isSelected,
        'ui-choice-list__options__option--disabled': props.option.disabled,
        'ui-choice-list__options__option--is-select-all': props.option.value === SELECT_ALL_OPTION_VALUE,
    } );
    
    // get display label
    const _label = props.option.title || props.option.label;
    let label = isObject( _label ) ? getLabel( _label ) : _label;
    if( ! isEmpty( props.queryString ) ) {
        label = textWithPatternMatch( { value: label, pattern: props.queryString } );
    }

    return (
        <div className={ widgetClassName }>
            
            {/* main option */}
            <div className='ui-choice-list__options__option__main-option' onClick={ props.onClick } /*title={ label }*/>
                {/* checkbox */}
                {
                    props.type !== DATA_TYPES.SINGLE_SELECT ? (
                        <div className='ui-choice-list__options__option__main-option__checkbox'>
                            <Checkbox size='s' value={ isSelected } />
                        </div>
                    ) : null
                }

                {/* label text */}
                <div className='ui-choice-list__options__option__main-option__label' dangerouslySetInnerHTML = { { __html: label } } />
            </div>

            {/* nested options */}
            {
                ( ! isEmpty( props.option.options ) && includes( [ DATA_TYPES.DEEP_SELECT, DATA_TYPES.DEEP_MULTI_SELECT ], props.type ) ) ? (
                    <div className='ui-choice-list__options__option__nested-options'>
                        {
                            props.option.options.map( _option => {
                                return (
                                    <ChoiceOption
                                        key={ `${ _option.name }_${ _option.value }` }
                                        option={ _option }
                                        selected={  props.selected }
                                        type={  props.type }
                                        onOptionSelect={  props.onOptionSelect }
                                        queryString={  props.queryString }
                                        onClick={ props.option.disabled ? noop : () => props.onOptionSelect( props.option, _option ) }
                                        isOptionSelected={ () => props.isOptionSelected( props.option, _option ) }
                                    />
                                );
                            } )
                        }
                    </div>
                ) : null
            }
        </div>
    );
};

// set display name
ChoiceOption.displayName = 'ChoiceOption';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { choiceListResizable as ChoiceListResizable } from 'modules/choice-list';
 *
 * // render
 * <ChoiceListResizable propName='propValue'/>
 */

export const choiceListResizable = React.forwardRef( ( props, forwardedRef ) => {
    const choiceListClassName = classnames( 'ui-choice-list', props.className );

    const { isFetching = false } = props;

    const [ resizeProps, setResizeProps ] = useState( { height: '110', maxConstraints: 300 } );

    const firstRenderRef = useRef( true );
    const parentChoiceListRef = useRef( null );
    const simpleBarRef = useRef( null );
    const localFilter = useRef( null );

    const onDropDownResize = ( event, { size } ) => {

        // DOM element references
        const siteFooter = document.querySelector( '.ui-site-footer' );
        const siteHeaderContent = document.querySelector( '.ui-site-header__content' );

        // Get DOM elments position in viewport
        const parentChoiceListBottomPosInViewPort = parentChoiceListRef.current && parentChoiceListRef.current.getBoundingClientRect().bottom;
        const parentChoiceListTopPosInViewPort = parentChoiceListRef.current && parentChoiceListRef.current.getBoundingClientRect().top;
        const siteFooterTopPosInViewPort = siteFooter && siteFooter.getBoundingClientRect().top;
        const siteHeaderTopPosInViewPort = siteHeaderContent && siteHeaderContent.getBoundingClientRect().top;

        // If list reached till end of viewport from top/bottom, stops expanding
        if ( ( ( size.height >= resizeProps.height ) && ( round( parentChoiceListBottomPosInViewPort ) >= round( siteFooterTopPosInViewPort ) ) ) || ( ( round( parentChoiceListTopPosInViewPort ) <= round( siteHeaderTopPosInViewPort ) ) ) ) {

            // Set only once when reached till footer
            if ( resizeProps.maxConstraints !== resizeProps.height ) {

                setResizeProps( { ...resizeProps, maxConstraints: resizeProps.height } );
            }

            return;
        }

        // Sets dynamic height to dropdown as resized
        setResizeProps( { ...resizeProps, height: size.height } );

    };


    // useEffect hook to run side-effects
    useEffect( () => {

        // DOM element reference
        const simBarContentElement = simpleBarRef.current && simpleBarRef.current.contentEl;

        if( simpleBarRef.current && simpleBarRef.current.el ) {

            // sets dynamic height, with effect
            if ( props.allowFilter ) {
                parentChoiceListRef.current.style.maxHeight = ( toNumber( resizeProps.height ) + toNumber( localFilter.current.offsetHeight ) ) + 'px';
            } else {
                parentChoiceListRef.current.style.maxHeight = resizeProps.height + 'px';
            }

            simpleBarRef.current.el.style.maxHeight = resizeProps.height + 'px';
        }

        // Sets once on first render cycle only
        if ( firstRenderRef.current ) {

            firstRenderRef.current = false;

            if ( simBarContentElement ) {

                setResizeProps( { ...resizeProps, maxConstraints: simBarContentElement.offsetHeight } );
            }
        }

    }, [ resizeProps.height ] );

    return (

            <div data-ignore-outclick='true' ref={ reactMergeRefs( [ forwardedRef, parentChoiceListRef ] ) } className= { choiceListClassName } style={ { width: props.width } }>

                {/* display filter */}
                {
                    props.allowFilter ? (
                        <div className='ui-choice-list__local-filter' ref= { localFilter } >
                            <SearchBar
                                placeholder={ props.placeholder }
                                value={ props.queryString }
                                size='small'
                                onChange={ ( query ) => props.onQueryChange( query ) }
                            />
                        </div>
                    ) : null
                }

                { // Renders resizable list based on options length and prop passed
                    ( ( 3 < size( props.options ) ) && get( props, 'resizableList', false ) ) ? (

                        <ResizableBox
                            width={ 100 }
                            height={ 110 }
                            minConstraints={ [ 110, 110 ] }
                            maxConstraints={ [ resizeProps.maxConstraints, resizeProps.maxConstraints ] }
                            onResize={ onDropDownResize }
                        >

                        {/* options */}
                        {
                            ! isEmpty( props.options ) ? (
                                <div data-elem-type='options-list' className='ui-choice-list__options'>
                                    {
                                        <EndScrollDetector
                                            disabled={ props.disablePagination }
                                            callback={ props.onPagination }
                                            render={ ( { ref } ) => (
                                                <SimpleBar scrollableNodeProps={ { ref } } ref= { simpleBarRef } style={ { maxHeight: 100 } } className='ui-choice-list__options__simBar'>
                                                    {
                                                        props.options.map( ( option ) => {
                                            
                                                            // render option
                                                            return (
                                                                <ChoiceOption
                                                                    key={ `${ option.name }_${ option.value }` }
                                                                    option={ option }
                                                                    activeIndex={  props.activeIndex }
                                                                    selected={  props.selected }
                                                                    type={  props.type }
                                                                    onOptionSelect={  props.onOptionSelect }
                                                                    queryString={  props.queryString }
                                                                    onClick={ option.disabled ? noop : () => props.onOptionSelect( option ) }
                                                                    isOptionSelected={ props.isOptionSelected }
                                                                />
                                                            );
                                                        } )
                                                    }
                                                </SimpleBar>
                                            ) }
                                        />
                                    }

                                    {/* pagination spinner */}
                                    {
                                        props.isFetchingMore /*&& 'pagination' === props.fetchingType*/ ? (
                                            <div className='ui-choice-list__loading'>
                                                <Spinner radius={ 15 } />
                                            </div>
                                        ) : null
                                    }
                                </div>
                            ) : (
                                <>
                                    { isFetching ? (

                                        // no options available
                                        <div className='ui-choice-list__loading'>
                                            <Spinner radius={ 15 } />
                                        </div>
                                    ) : (

                                        // no options available
                                        <>
                                            { ! isEmpty( props.queryString ) ? (
                                                <div className='ui-choice-list__no-data'>
                                                { getLabel( { refId: GLOBAL_LABELS.DISPLAY_TEXT_NO_MATCH_FOUND, value: '' } ) }
                                                </div>
                                            ) : null }
                                        </>
                                    ) }
                                </>
                            )
                        }

                        </ResizableBox>
                    ) : (
                        <>
                            {/* options */}
                            {
                                ! isEmpty( props.options ) ? (
                                    <div data-elem-type='options-list' className='ui-choice-list__options'>
                                        {
                                            <EndScrollDetector
                                                disabled={ props.disablePagination }
                                                callback={ props.onPagination }
                                                render={ ( { ref } ) => (
                                                    <SimpleBar scrollableNodeProps={ { ref } } ref= { simpleBarRef } style={ { maxHeight: 100 } } className='ui-choice-list__options__simBar'>
                                                        {
                                                            props.options.map( ( option ) => {
                                                
                                                                // render option
                                                                return (
                                                                    <ChoiceOption
                                                                        key={ `${ option.name }_${ option.value }` }
                                                                        option={ option }
                                                                        activeIndex={  props.activeIndex }
                                                                        selected={  props.selected }
                                                                        type={  props.type }
                                                                        onOptionSelect={  props.onOptionSelect }
                                                                        queryString={  props.queryString }
                                                                        onClick={ option.disabled ? noop : () => props.onOptionSelect( option ) }
                                                                        isOptionSelected={ props.isOptionSelected }
                                                                    />
                                                                );
                                                            } )
                                                        }
                                                    </SimpleBar>
                                                ) }
                                            />
                                        }

                                        {/* pagination spinner */}
                                        {
                                            props.isFetchingMore /*&& 'pagination' === props.fetchingType*/ ? (
                                                <div className='ui-choice-list__loading'>
                                                    <Spinner radius={ 15 } />
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                ) : (
                                    <>
                                        { isFetching ? (

                                            // no options available
                                            <div className='ui-choice-list__loading'>
                                                <Spinner radius={ 15 } />
                                            </div>
                                        ) : (

                                            // no options available
                                            <>
                                                { ! isEmpty( props.queryString ) ? (
                                                    <div className='ui-choice-list__no-data'>
                                                    { getLabel( { refId: GLOBAL_LABELS.DISPLAY_TEXT_NO_MATCH_FOUND, value: '' } ) }
                                                    </div>
                                                ) : null }
                                            </>
                                        ) }
                                    </>
                                )
                            }
                        </>
                    )
                }


            </div>
    );
} );

// set display name
choiceListResizable.displayName = 'choiceListResizable';

// set default props
choiceListResizable.defaultProps = {
    allowFilter: false,
    onQueryChange: noop,
    width: undefined,
};

// prop types
choiceListResizable.propTypes = {};
